/**
 * Send an authenticated raw-`fetch` request, refreshing the session once on a 401.
 *
 * The chat stream endpoints (send, reattach, questionnaire answers) are raw
 * `fetch`, so the axios interceptor that refreshes a lapsed access token and
 * replays the request never sees them. Without this, a tab left open past its
 * token's lifetime failed its next send while every other request on the page
 * recovered.
 *
 * A 401 is issued by `@login_required` before the handler runs, so the refused
 * request created nothing and sending it again is safe. Retried once: a second
 * 401 after a successful refresh is a fresh cookie that still is not arriving
 * (a cookie jar past the browser's per-domain limit), and is returned for the
 * caller to report. A refresh that fails returns the original 401 the same way.
 */
export async function fetchWithSessionRefresh(
  send: () => Promise<Response>,
  refreshSession: () => Promise<void>,
): Promise<Response> {
  const response = await send();
  if (response.status !== 401) {
    return response;
  }
  try {
    await refreshSession();
  } catch (refreshError) {
    console.warn('🔑 [session] Refresh failed after a refused request', refreshError);
    return response;
  }
  console.warn('🔑 [session] Request refused as unauthenticated — session refreshed, sending again');
  return send();
}
